import { Component, ComponentType } from "@/types";
import { useEditorStore } from "@/stores/editor/editor-store";
import type { ComponentUpdatePayload } from "@/types/component-update";
import type {
  SemanticReference,
  ReferenceResolutionResult,
  BreakpointId,
  ResponsiveStyleChanges,
  TextSelectionContext,
  TextOperationType
} from "@/types/ai";

export function isForbiddenPrototypeKey(key: string): boolean {
  return key === "__proto__" || key === "constructor" || key === "prototype";
}

function isMergeableRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Phase 2: Responsive chart configuration per breakpoint
 * Allows breakpoint-specific chart settings for mobile/tablet/desktop
 */
export interface ChartResponsiveConfig {
  // Visibility toggles
  showLegend?: boolean;
  showGrid?: boolean;
  showTooltip?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;
  showDots?: boolean;

  // Size-related properties
  strokeWidth?: number;
  dotSize?: number;
  axisFontSize?: number;
  legendFontSize?: number;

  // Layout
  margin?: { top?: number; right?: number; bottom?: number; left?: number };

  // Title
  title?: string | null;

  // Line style properties (Phase 1)
  strokeDasharray?: string;
  strokeOpacity?: number;
  lineType?: string;

  // Style overrides
  style?: Record<string, unknown>;
}

export interface PropertyChange {
  properties?: Record<string, unknown>;
  style?: Record<string, unknown>;
  size?: { width?: number | string; height?: number | string };
  position?: { x?: number; y?: number; z?: number };
  responsiveProperties?: {
    [viewport: string]: Record<string, unknown>;
  };
  /** Phase 5.4: Direct responsive position updates */
  responsivePosition?: Record<string, unknown>;
  data?: unknown[];
  type?: ComponentType;
  /** Target component ID when applying changes to a specific component */
  targetComponentId?: string;
  /** Semantic resolution info for the target component (Phase 2.1) */
  semanticResolution?: SemanticReference;
  /** Phase 3.1: Target breakpoint for this change (undefined = all breakpoints) */
  breakpoint?: BreakpointId;
  /** Phase 3.1: Styles to apply at specific breakpoints */
  responsiveStyles?: ResponsiveStyleChanges;
  /** Phase 4: Warnings to display to the user (e.g., margin on full-width container) */
  _warnings?: string[];

  // ========== Phase 5: Text Selection & Quoted Content Enhancement ==========

  /** Phase 5: Text selection context when modifying specific text portions */
  textSelection?: TextSelectionContext;

  /** Phase 5: Content modification type for text operations */
  textOperation?: TextOperationType;

  /** Phase 5: For style-range operations, styles to apply to selected text */
  rangeStyles?: Record<string, unknown>;

  // ========== Phase 2: Responsive Chart Commands ==========

  /** Phase 2: Responsive chart configuration per breakpoint */
  responsiveChartConfig?: {
    mobile?: ChartResponsiveConfig;
    tablet?: ChartResponsiveConfig;
    desktop?: ChartResponsiveConfig;
  };

  // ========== Component Type Conversion ==========

  /** Convert DATA_TEXT → TEXT (disconnect data binding) */
  convertToText?: boolean;
}

/**
 * Extended command result with semantic resolution info (Phase 2.1)
 */
export interface SemanticCommandResult {
  success: boolean;
  command?: {
    type: "property" | "style" | "content" | "data" | "conversion" | "template";
    changes?: PropertyChange;
    confidence: number;
    description?: string;
  };
  error?: string;
  suggestions?: string[];
  /** If semantic resolution was needed and found matches */
  resolutionResult?: ReferenceResolutionResult;
  /** Whether clarification is needed before applying changes */
  needsClarification?: boolean;
}

export interface CommandResult {
  success: boolean;
  action?: 'modify' | 'add' | 'delete' | 'template' | 'help' | 'unknown';
  command?: {
    type: "property" | "style" | "content" | "data" | "conversion" | "template" | "action";
    changes?: PropertyChange;
    confidence: number;
    description?: string;
    /** Action identifier for non-property-change commands (e.g., OAuth flows) */
    action?: string;
  };
  changes?: PropertyChange;
  component?: Partial<Component>;
  components?: Partial<Component>[];
  additionalUpdates?: Array<{
    componentId: string;
    changes: Partial<ComponentUpdatePayload>;
    targetComponent?: Component;
  }>;
  containerId?: string;
  message?: string;
  error?: string;
  suggestions?: string[];
}

/**
 * Abstract base class for all component command handlers
 * Provides common functionality and enforces consistent interface
 */
export abstract class BaseCommandHandler {
  protected commandPatterns: Map<RegExp, (match: RegExpMatchArray, component: Component) => PropertyChange | null> = new Map();

  /**
   * Check if this handler can process the given command
   */
  abstract canHandle(command: string, component: Component): boolean;

  /**
   * Parse a command and return property changes
   */
  abstract parseCommand(command: string, component: Component): Promise<CommandResult>;

  /**
   * Get example commands for a component type
   */
  abstract getExampleCommands(componentType: ComponentType): Promise<string[]>;

  /**
   * Get contextual suggestions based on component state
   */
  abstract getSuggestions(component: Component): Promise<string[]>;

  /**
   * Common method to validate changes before applying
   */
  protected validateChanges(changes: PropertyChange, _component: Component): { valid: boolean; error?: string } {
    // Basic validation - can be overridden by subclasses
    if (changes.size) {
      if (changes.size.width !== undefined) {
        const width = typeof changes.size.width === 'number' ? changes.size.width : parseInt(String(changes.size.width), 10);
        if (isNaN(width) || width < 0) {
          return { valid: false, error: "Invalid width value" };
        }
      }
      if (changes.size.height !== undefined) {
        const height = typeof changes.size.height === 'number' ? changes.size.height : parseInt(String(changes.size.height), 10);
        if (isNaN(height) || height < 0) {
          return { valid: false, error: "Invalid height value" };
        }
      }
    }

    return { valid: true };
  }

  /**
   * Calculate confidence score for a command
   */
  protected calculateConfidence(command: string, changes: PropertyChange): number {
    let confidence = 0.5; // Base confidence

    // Increase confidence for specific command patterns
    if (command.includes("set") || command.includes("change") || command.includes("make")) {
      confidence += 0.1;
    }

    // Increase for quoted values
    if (command.includes('"') || command.includes("'")) {
      confidence += 0.15;
    }

    // Increase for numeric values
    if (/\d+/.test(command)) {
      confidence += 0.1;
    }

    // Increase based on number of changes
    const changeCount =
      Object.keys(changes.properties || {}).length +
      Object.keys(changes.style || {}).length +
      (changes.size ? 1 : 0) +
      (changes.position ? 1 : 0) +
      (changes.data ? 1 : 0);

    if (changeCount > 0) {
      confidence += Math.min(changeCount * 0.05, 0.25);
    }

    return Math.min(confidence, 1.0);
  }

  /**
   * Determine command type from changes
   */
  protected determineCommandType(changes: PropertyChange): "property" | "style" | "content" | "data" | "conversion" | "template" {
    if (changes.type) return "conversion";
    if (changes.data) return "data";
    if (changes.style && Object.keys(changes.style).length > 0) return "style";
    if (changes.properties?.content || changes.properties?.text) return "content";
    return "property";
  }

  /**
   * Generate description of changes
   */
  protected generateDescription(changes: PropertyChange, _component: Component): string {
    const descriptions: string[] = [];

    if (changes.type) {
      descriptions.push(`Convert to ${changes.type}`);
    }

    if (changes.properties) {
      const props = Object.entries(changes.properties);
      props.forEach(([key, value]) => {
        if (typeof value === 'string' && value.length > 20) {
          descriptions.push(`Set ${key} to "${value.substring(0, 20)}..."`);
        } else {
          descriptions.push(`Set ${key} to ${JSON.stringify(value)}`);
        }
      });
    }

    if (changes.style) {
      const styles = Object.entries(changes.style);
      styles.forEach(([key, value]) => {
        descriptions.push(`Style ${key}: ${value}`);
      });
    }

    if (changes.size) {
      if (changes.size.width) descriptions.push(`Width: ${changes.size.width}`);
      if (changes.size.height) descriptions.push(`Height: ${changes.size.height}`);
    }

    return descriptions.join("; ") || "Update component";
  }

  /**
   * Parse numeric value from string with units
   */
  protected parseNumericValue(value: string): number | string | null {
    const match = value.match(/(\d+(?:\.\d+)?)\s*(px|%|em|rem|vh|vw)?/i);
    if (match) {
      const num = parseFloat(match[1]);
      const unit = match[2];
      return unit ? `${num}${unit}` : num;
    }
    return null;
  }

  /**
   * Parse color value from string
   */
  protected parseColorValue(value: string): string | null {
    // Hex color
    if (/#[0-9A-Fa-f]{3,8}/.test(value)) {
      return value.match(/#[0-9A-Fa-f]{3,8}/)?.[0] || null;
    }

    // RGB/RGBA
    const rgbMatch = value.match(/rgba?\([^)]+\)/i);
    if (rgbMatch) {
      return rgbMatch[0];
    }

    // Named colors - includes all 147 CSS named colors for maximum compatibility
    const namedColors = [
      // Basic colors
      'red', 'blue', 'green', 'yellow', 'orange', 'purple', 'pink',
      'black', 'white', 'gray', 'grey', 'brown', 'cyan', 'magenta',
      // Light variants
      'lightblue', 'lightgreen', 'lightgray', 'lightgrey', 'lightcyan',
      'lightcoral', 'lightgoldenrodyellow', 'lightpink', 'lightsalmon',
      'lightseagreen', 'lightskyblue', 'lightslategray', 'lightslategrey',
      'lightsteelblue', 'lightyellow',
      // Dark variants
      'darkblue', 'darkgreen', 'darkgray', 'darkgrey', 'darkcyan',
      'darkgoldenrod', 'darkkhaki', 'darkmagenta', 'darkolivegreen',
      'darkorange', 'darkorchid', 'darkred', 'darksalmon', 'darkseagreen',
      'darkslateblue', 'darkslategray', 'darkslategrey', 'darkturquoise',
      'darkviolet',
      // Other common colors
      'aqua', 'aquamarine', 'azure', 'beige', 'bisque', 'blanchedalmond',
      'blueviolet', 'burlywood', 'cadetblue', 'chartreuse', 'chocolate',
      'coral', 'cornflowerblue', 'cornsilk', 'crimson', 'deeppink',
      'deepskyblue', 'dimgray', 'dimgrey', 'dodgerblue', 'firebrick',
      'floralwhite', 'forestgreen', 'fuchsia', 'gainsboro', 'ghostwhite',
      'gold', 'goldenrod', 'greenyellow', 'honeydew', 'hotpink',
      'indianred', 'indigo', 'ivory', 'khaki', 'lavender', 'lavenderblush',
      'lawngreen', 'lemonchiffon', 'lime', 'limegreen', 'linen', 'maroon',
      'mediumaquamarine', 'mediumblue', 'mediumorchid', 'mediumpurple',
      'mediumseagreen', 'mediumslateblue', 'mediumspringgreen',
      'mediumturquoise', 'mediumvioletred', 'midnightblue', 'mintcream',
      'mistyrose', 'moccasin', 'navajowhite', 'navy', 'oldlace', 'olive',
      'olivedrab', 'orangered', 'orchid', 'palegoldenrod', 'palegreen',
      'paleturquoise', 'palevioletred', 'papayawhip', 'peachpuff', 'peru',
      'plum', 'powderblue', 'rebeccapurple', 'rosybrown', 'royalblue',
      'saddlebrown', 'salmon', 'sandybrown', 'seagreen', 'seashell',
      'sienna', 'silver', 'skyblue', 'slateblue', 'slategray', 'slategrey',
      'snow', 'springgreen', 'steelblue', 'tan', 'teal', 'thistle',
      'tomato', 'turquoise', 'violet', 'wheat', 'whitesmoke', 'yellowgreen',
      // Transparent
      'transparent'
    ];
    const colorWord = value.toLowerCase().split(' ').find(word => namedColors.includes(word));
    return colorWord || null;
  }

  /**
   * Extract quoted string from command
   */
  protected extractQuotedString(command: string): string | null {
    const match = command.match(/["']([^"']+)["']/);
    return match ? match[1] : null;
  }

  /**
   * Check if command matches any pattern.
   * Prefers the longest match (most specific) rather than first match,
   * so "inactive color" beats "active color" regardless of insertion order.
   */
  protected matchPatterns(command: string, component: Component): PropertyChange | null {
    let bestResult: PropertyChange | null = null;
    let bestLength = -1;

    for (const [pattern, handler] of this.commandPatterns) {
      const match = command.match(pattern);
      if (match && match[0].length > bestLength) {
        const result = handler(match, component);
        if (result) {
          bestResult = result;
          bestLength = match[0].length;
        }
      }
    }
    return bestResult;
  }

  /**
   * Extract potential component reference from command (Phase 2.1)
   * Returns the reference text if found, null otherwise
   */
  protected extractComponentReference(command: string): string | null {
    const normalizedCommand = command.toLowerCase().trim();

    // Common patterns for component references
    const patterns = [
      // "the X", "the X button", etc.
      /(?:the|this|that)\s+([a-z]+(?:\s+[a-z]+)?)\s*(?:button|component|element|section|panel|container)?/i,
      // "make X bigger" where X is a component reference
      /(?:make|change|update|modify)\s+(?:the\s+)?([a-z]+(?:\s+[a-z]+)?)\s+/i,
      // Direct references like "header", "submit button"
      /^([a-z]+(?:\s+[a-z]+)?)$/i,
    ];

    for (const pattern of patterns) {
      const match = normalizedCommand.match(pattern);
      if (match && match[1]) {
        // Filter out action words that aren't component references
        const ref = match[1].trim();
        const actionWords = ['it', 'this', 'that', 'them', 'all', 'bigger', 'smaller', 'larger', 'primary', 'secondary'];
        if (!actionWords.includes(ref)) {
          console.log(`[BaseCommandHandler] Extracted component reference: "${ref}"`);
          return ref;
        }
      }
    }

    return null;
  }

  /**
   * Check if command contains a component reference that might need resolution
   */
  protected hasComponentReference(command: string): boolean {
    return this.extractComponentReference(command) !== null;
  }

  /**
   * Resolve page name to page ID
   * Supports exact match and partial match
   * Used by ButtonCommandHandler, IconCommandHandler, and chart handlers for navigation
   * Supports both spaces and dashes in page names (e.g., "Settings Page" or "Settings-Page")
   */
  protected resolvePageTarget(pageName: string): { pageId: string | null; pageName: string | null; error?: string } {
    const editorStore = useEditorStore.getState();
    const pages = editorStore.pages || [];

    if (pages.length === 0) {
      return { pageId: null, pageName: null, error: "No pages available in project" };
    }

    const normalizeToken = (token: string) => {
      if (token.endsWith('ies') && token.length > 3) {
        return `${token.slice(0, -3)}y`;
      }
      if (token.endsWith('s') && !token.endsWith('ss') && token.length > 3) {
        return token.slice(0, -1);
      }
      return token;
    };

    const normalizePageReference = (value: string) => value
      .toLowerCase()
      .trim()
      .replace(/^#+/, '')
      .replace(/^the\s+/, '')
      .replace(/\s+page$/, '')
      .replace(/[-_]+/g, ' ')
      .replace(/[^\w\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const tokenizePageReference = (value: string) => normalizePageReference(value)
      .split(' ')
      .map(token => normalizeToken(token.trim()))
      .filter(Boolean);

    const buildComparablePageName = (value: string) => tokenizePageReference(value).join(' ');

    const normalizedSearch = buildComparablePageName(pageName);

    // Try exact match first (case-insensitive, dash/space agnostic)
    const exactMatch = pages.find(
      p => buildComparablePageName(p.name) === normalizedSearch
    );
    if (exactMatch) {
      return { pageId: exactMatch.id, pageName: exactMatch.name };
    }

    // Try partial match (case-insensitive, dash/space agnostic)
    const partialMatch = pages.find(
      p => {
        const normalizedPageName = buildComparablePageName(p.name);
        return normalizedPageName.includes(normalizedSearch) ||
          normalizedSearch.includes(normalizedPageName);
      }
    );
    if (partialMatch) {
      return { pageId: partialMatch.id, pageName: partialMatch.name };
    }

    const searchTokens = new Set(tokenizePageReference(pageName));
    const tokenMatchedPage = pages
      .map((page) => {
        const pageTokens = new Set(tokenizePageReference(page.name));
        const overlap = [...searchTokens].filter(token => pageTokens.has(token)).length;
        const score = overlap === 0
          ? 0
          : overlap / Math.max(searchTokens.size, pageTokens.size);
        return { page, overlap, score };
      })
      .sort((a, b) => b.score - a.score || b.overlap - a.overlap)[0];

    if (tokenMatchedPage && tokenMatchedPage.score >= 0.75) {
      return {
        pageId: tokenMatchedPage.page.id,
        pageName: tokenMatchedPage.page.name,
      };
    }

    // No match found - generate helpful error with # syntax hints
    const currentPageId = editorStore.currentPageId;
    const availablePages = pages
      .filter(p => p.id !== currentPageId) // Exclude current page
      .map(p => `#${p.name.replace(/\s+/g, '-')}`)
      .join(', ');

    return {
      pageId: null,
      pageName: null,
      error: `Page "${pageName}" not found. Available pages: ${availablePages || 'none'}`
    };
  }

  /**
   * Public entry point for external callers (e.g., PropertyModificationService)
   * that need to run pattern matching on handler instances.
   * Keeps callers on a typed API instead of reaching into protected methods.
   */
  public tryMatchPatterns(command: string, component: Component): PropertyChange | null {
    return this.matchPatterns(command, component);
  }

  /**
   * Public entry point for external callers that need page resolution.
   * Keeps callers on a typed API instead of reaching into protected methods.
   */
  public tryResolvePageTarget(pageName: string): { pageId: string | null; pageName: string | null; error?: string } {
    return this.resolvePageTarget(pageName);
  }

  /**
   * Return component.properties when it is a string-keyed object.
   *
   * Callers that need a component-specific shape can pass a type guard:
   *   const props = this.typedProperties(component, isMapComponentProperties);
   */
  protected typedProperties(component: Component): Record<string, unknown> | undefined;
  protected typedProperties<T extends Record<string, unknown>>(
    component: Component,
    guard: (value: Record<string, unknown>) => value is T
  ): T | undefined;
  protected typedProperties<T extends Record<string, unknown>>(
    component: Component,
    guard?: (value: Record<string, unknown>) => value is T
  ): Record<string, unknown> | T | undefined {
    const p = component.properties;
    if (isMergeableRecord(p)) {
      return guard ? (guard(p) ? p : undefined) : p;
    }
    return undefined;
  }

  /**
   * Return component.style when it is a string-keyed object.
   */
  protected typedStyle(component: Component): Record<string, unknown> | undefined;
  protected typedStyle<T extends Record<string, unknown>>(
    component: Component,
    guard: (value: Record<string, unknown>) => value is T
  ): T | undefined;
  protected typedStyle<T extends Record<string, unknown>>(
    component: Component,
    guard?: (value: Record<string, unknown>) => value is T
  ): Record<string, unknown> | T | undefined {
    const s = component.style;
    if (isMergeableRecord(s)) {
      return guard ? (guard(s) ? s : undefined) : s;
    }
    return undefined;
  }

  /**
   * Recursively merge source into target.
   * Both parameters are constrained to Record<string, unknown> so that
   * callers cannot pass untyped objects.
   *
   * Usage (replaces the private deepMerge in MapCommandHandler):
   *   changes.properties = this.deepMergeProps(
   *     changes.properties ?? {},
   *     patternMatch.properties ?? {}
   *   );
   */
  protected deepMergeProps(
    target: Record<string, unknown>,
    source: Record<string, unknown>
  ): Record<string, unknown> {
    const result: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(target)) {
      if (!isForbiddenPrototypeKey(key)) {
        result[key] = value;
      }
    }

    for (const key of Object.keys(source)) {
      if (isForbiddenPrototypeKey(key)) {
        continue;
      }

      const sv = source[key];
      const tv = result[key];
      if (isMergeableRecord(sv) && isMergeableRecord(tv)) {
        result[key] = this.deepMergeProps(tv, sv);
      } else {
        result[key] = sv;
      }
    }
    return result;
  }
}
